import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, TextArea, SectionTitle } from "@/lib/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Bot, Play, Terminal, Cpu, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_TONE = { IDLE: "ok", DISABLED: "muted", RESERVED: "gold", ERROR: "err", RUNNING: "proc" };

function AgentDetail({ code, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const { data: agent, isLoading } = useQuery({ queryKey: ["agent", code], queryFn: () => api.get(`/agents/${code}`) });
  const [prompt, setPrompt] = useState("Give a one-line status of your domain.");
  const [instruction, setInstruction] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const patch = useMutation({
    mutationFn: (body) => api.patch(`/agents/${code}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent", code] }); qc.invalidateQueries({ queryKey: ["agents"] }); toast.success("Agent updated"); },
    onError: (e) => toast.error(e.message),
  });
  const runTest = useMutation({
    mutationFn: () => api.post(`/agents/${code}/test`, { prompt }),
    onSuccess: (r) => { setTestResult(r); qc.invalidateQueries({ queryKey: ["agent", code] }); toast.success("Agent responded via MeshAPI"); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading || !agent) return <PageLoader />;
  const instr = instruction ?? agent.system_instruction;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Model", agent.model || "openai/gpt-4o (default)"],
          ["Environment", agent.environment],
          ["Total Runs", agent.total_runs],
          ["Tokens Used", agent.total_tokens],
          ["Errors", agent.error_count],
          ["Instr. Version", `v${agent.instruction_version}`],
          ["Last Run", agent.last_run ? new Date(agent.last_run).toLocaleString() : "—"],
          ["Status", agent.status],
        ].map(([k, v]) => (
          <div key={k} className="border border-border rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
            <div className="font-mono text-xs mt-1 truncate">{String(v)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border border-border rounded-md p-3">
        <div>
          <div className="text-sm font-medium">Enabled</div>
          <div className="text-xs text-muted-foreground">Disabled agents cannot execute runs.</div>
        </div>
        <Switch
          data-testid="agent-toggle-enabled"
          checked={agent.enabled}
          onCheckedChange={(v) => patch.mutate({ enabled: v })}
        />
      </div>

      <div>
        <SectionTitle className="mb-2">System Instruction</SectionTitle>
        <TextArea rows={5} value={instr} onChange={(e) => setInstruction(e.target.value)} data-testid="agent-instruction" />
        <div className="flex justify-end mt-2">
          <Btn size="sm" variant="outline" disabled={instr === agent.system_instruction}
            onClick={() => patch.mutate({ system_instruction: instr })} data-testid="save-instruction">
            Save Instruction
          </Btn>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <SectionTitle className="mb-2">Test Agent (real MeshAPI request)</SectionTitle>
        <TextArea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="agent-test-prompt" />
        <div className="flex justify-end mt-2">
          <Btn size="sm" onClick={() => runTest.mutate()} disabled={runTest.isPending || !agent.enabled} data-testid="agent-run-test">
            {runTest.isPending ? <Cpu className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run Test
          </Btn>
        </div>
        {testResult && (
          <div className="mt-3 border border-proc/30 bg-proc/5 rounded-md p-3">
            <div className="text-[10px] font-mono uppercase text-proc mb-1">Response · {testResult.usage?.total_tokens} tokens · {testResult.model}</div>
            <div className="text-sm whitespace-pre-wrap">{testResult.response}</div>
          </div>
        )}
      </div>

      <div>
        <SectionTitle className="mb-2">Recent Runs</SectionTitle>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {(agent.recent_runs || []).length === 0 && <div className="text-xs text-muted-foreground">No runs recorded.</div>}
          {(agent.recent_runs || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs font-mono border-b border-border/50 pb-1">
              <span className="text-muted-foreground">{r.kind}</span>
              <StatusBadge tone={r.status === "SUCCESS" ? "ok" : "err"}>{r.status}</StatusBadge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Agents() {
  const api = useApi();
  const [open, setOpen] = useState(null);
  const { data: agents = [], isLoading } = useQuery({ queryKey: ["agents"], queryFn: () => api.get("/agents") });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker="Control Centre" title="AI Agents">
        <StatusBadge tone="ok">{agents.filter((a) => a.enabled).length} Active</StatusBadge>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {agents.map((a) => (
          <Dialog key={a.code} open={open === a.code} onOpenChange={(o) => setOpen(o ? a.code : null)}>
            <DialogTrigger asChild>
              <button
                data-testid={`agent-card-${a.code}`}
                className="text-left bg-surface border border-border rounded-md p-4 hover:bg-accent/40 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-primary">{a.code}</span>
                  <StatusBadge tone={STATUS_TONE[a.status] || "muted"} dot={a.status === "RUNNING" ? "pulse" : true}>
                    {a.status}
                  </StatusBadge>
                </div>
                <div className="font-head text-lg font-bold mt-2 leading-tight">{a.name}</div>
                <div className="text-xs text-muted-foreground mt-1 capitalize">{a.role.replace(/_/g, " ")}</div>
                <div className="flex items-center gap-4 mt-3 text-[11px] font-mono text-muted-foreground">
                  <span>{a.total_runs} runs</span>
                  <span>{a.total_tokens} tok</span>
                  {a.error_count > 0 && <span className="text-err flex items-center gap-1"><AlertCircle className="h-3 w-3" />{a.error_count}</span>}
                </div>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-popover border-border max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" /> {a.code} · {a.name}
                </DialogTitle>
              </DialogHeader>
              {open === a.code && <AgentDetail code={a.code} onClose={() => setOpen(null)} />}
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </div>
  );
}
